/**
 * Email preview modal component
 */
import React, { useEffect, useState } from 'react';
import { Modal, Tabs, Spin, Typography, Descriptions, Button, message } from 'antd';
import { CopyOutlined, SendOutlined } from '@ant-design/icons';
import type { Machine, EmailTemplate } from '../types';
import { machineApi } from '../api';

const { Text } = Typography;

interface EmailPreviewProps {
  visible: boolean;
  machine: Machine | null;
  onCancel: () => void;
}

const EmailPreview: React.FC<EmailPreviewProps> = ({
  visible,
  machine,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    if (visible && machine) {
      fetchEmailTemplate();
    }
  }, [visible, machine]);

  const fetchEmailTemplate = async () => {
    if (!machine) return;
    setLoading(true);
    try {
      const template = await machineApi.getEmailPreview(machine.id);
      setEmailTemplate(template);
    } catch (error) {
      message.error('Failed to load email template');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!machine) return;
    setSending(true);
    try {
      const result = await machineApi.sendEmail(machine.id);
      if (result.success) {
        message.success(result.message);
        onCancel();
      } else {
        message.error(result.message || 'Failed to send email');
      }
    } catch {
      message.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const tabItems = [
    {
      key: 'preview',
      label: 'HTML Preview',
      children: loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : emailTemplate ? (
        <div
          style={{
            background: '#fff',
            padding: 16,
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            maxHeight: 400,
            overflow: 'auto',
          }}
          dangerouslySetInnerHTML={{ __html: emailTemplate.body_html }}
        />
      ) : null,
    },
    {
      key: 'text',
      label: 'Plain Text',
      children: loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : emailTemplate ? (
        <div style={{ position: 'relative' }}>
          <Button
            size="small"
            icon={<CopyOutlined />}
            style={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => copyToClipboard(emailTemplate.body_text, 'Plain text')}
          >
            Copy
          </Button>
          <pre
            style={{
              background: '#f5f5f5',
              padding: 16,
              borderRadius: 4,
              maxHeight: 400,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
            }}
          >
            {emailTemplate.body_text}
          </pre>
        </div>
      ) : null,
    },
    {
      key: 'html',
      label: 'HTML Source',
      children: loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : emailTemplate ? (
        <div style={{ position: 'relative' }}>
          <Button
            size="small"
            icon={<CopyOutlined />}
            style={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => copyToClipboard(emailTemplate.body_html, 'HTML')}
          >
            Copy
          </Button>
          <pre
            style={{
              background: '#f5f5f5',
              padding: 16,
              borderRadius: 4,
              maxHeight: 400,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontSize: 12,
            }}
          >
            {emailTemplate.body_html}
          </pre>
        </div>
      ) : null,
    },
  ];

  return (
    <Modal
      title="Email Preview"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Close
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          loading={sending}
          onClick={handleSendEmail}
          disabled={!emailTemplate}
        >
          Send Email
        </Button>,
      ]}
      width={700}
    >
      {emailTemplate && (
        <Descriptions
          bordered
          size="small"
          column={1}
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label="To">
            <Text copyable>{emailTemplate.to_email}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Subject">
            <Text copyable>{emailTemplate.subject}</Text>
          </Descriptions.Item>
        </Descriptions>
      )}

      <Tabs items={tabItems} />
    </Modal>
  );
};

export default EmailPreview;
